/**
 * Hook that manages offline sync lifecycle.
 * Automatically syncs pending entries when coming back online.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useOnlineStatus } from './useOnlineStatus';
import { usePendingSync } from './usePendingSync';
import { syncPendingEntries } from '../lib/syncService';

export function useSyncService() {
  const isOnline = useOnlineStatus();
  const { pendingCount, refresh } = usePendingSync();
  const syncTimeoutRef = useRef<number | null>(null);
  const lastOnlineRef = useRef(isOnline);

  const triggerSync = useCallback(async () => {
    if (!isOnline) return { synced: 0, failed: 0 };

    const result = await syncPendingEntries();
    await refresh();
    return result;
  }, [isOnline, refresh]);

  // Sync when coming back online
  useEffect(() => {
    const wasOffline = !lastOnlineRef.current;
    lastOnlineRef.current = isOnline;

    // Just came back online with pending entries
    if (isOnline && wasOffline && pendingCount > 0) {
      // Debounce to avoid rapid fire on connection flapping
      if (syncTimeoutRef.current) {
        window.clearTimeout(syncTimeoutRef.current);
      }

      syncTimeoutRef.current = window.setTimeout(() => {
        triggerSync();
      }, 1000);
    }

    return () => {
      if (syncTimeoutRef.current) {
        window.clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [isOnline, pendingCount, triggerSync]);

  // Periodic retry: if online with pending entries, retry every 30s
  useEffect(() => {
    if (!isOnline || pendingCount === 0) return;

    const interval = window.setInterval(() => {
      triggerSync();
    }, 30_000);

    return () => window.clearInterval(interval);
  }, [isOnline, pendingCount, triggerSync]);

  return {
    isOnline,
    pendingCount,
    triggerSync,
  };
}
